import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import {
  Trash2, Search, X, RefreshCw, Clock, HardHat, CheckCircle2,
} from "lucide-react";

// ─── Design tokens (matching ReportApproval exactly) ─────────────────────────
const DS = {
  violet:      "#5C2D91",
  violetLight: "#EDE6F7",
  violetMid:   "#7B3FB5",
  red:         "#C8102E",
  redLight:    "#FCEAED",
  orange:      "#E8640A",
  orangeLight: "#FEF0E6",
  green:       "#1A7A42",
  greenLight:  "#E2F5EC",
  dark:        "#1A0A2E",
  muted:       "#6B5A8A",
  faint:       "#C4B8DC",
  border:      "rgba(92,45,145,0.10)",
  borderSolid: "#E8E0F5",
  surface:     "#FFFFFF",
  surfaceAlt:  "#FAF8FD",
  gray:        "#64748B",
};

// ─── Stage badge colours ──────────────────────────────────────────────────────
const stageCfg = (stage = "") => {
  const s = stage.toLowerCase();
  if (s.includes("pre"))  return { bg: "#EDE6F7", color: DS.violet,  label: stage };
  if (s.includes("wip"))  return { bg: "#FEF0E6", color: DS.orange,  label: "WIP" };
  if (s.includes("post")) return { bg: "#E2F5EC", color: DS.green,   label: stage };
  return { bg: DS.surfaceAlt, color: DS.muted, label: stage || "—" };
};

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function DeletedReports() {
  const [reports,  setReports]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [hovRow,   setHovRow]   = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);      // NEW
const PER_PAGE = 10;    

  // ── Fetch deleted reports ──────────────────────────────────────────────────
  const fetchDeleted = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await API.get("/reports/deleted", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setReports(res.data.reports || []);
      }
    } catch (err) {
      console.error("[DeletedReports] fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeleted(); }, []);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = reports.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.title           || "").toLowerCase().includes(q) ||
      (r.report_type     || "").toLowerCase().includes(q) ||
      (r.project_name    || "").toLowerCase().includes(q) ||
      (r.engineer_name   || "").toLowerCase().includes(q) ||
      (r.deleted_by_name || "").toLowerCase().includes(q) ||
      (r.report_stage    || "").toLowerCase().includes(q)
    );
  });

  // NEW — pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  // ── KPI data — same counts as before, now styled like ProjectReports.jsx ──
  const kpiCards = [
    {
      label: "Total Deleted",
      value: reports.length,
      icon: <Trash2 size={20} />,
      color: DS.red,
      bg: `linear-gradient(135deg,${DS.redLight},#FFE8EC)`,
      border: "#FECDD3",
    },
    {
      label: "Pre Inspection",
      value: reports.filter(r => (r.report_stage||"").toLowerCase().includes("pre")).length,
      icon: <Search size={20} />,
      color: DS.violet,
      bg: `linear-gradient(135deg,${DS.violetLight},#F0E8FF)`,
      border: "#D8C8F5",
    },
    {
      label: "WIP",
      value: reports.filter(r => (r.report_stage||"").toLowerCase().includes("wip")).length,
      icon: <HardHat size={20} />,
      color: DS.orange,
      bg: `linear-gradient(135deg,${DS.orangeLight},#FFF3E8)`,
      border: "#F5C898",
    },
    {
      label: "Post Completion",
      value: reports.filter(r => (r.report_stage||"").toLowerCase().includes("post")).length,
      icon: <CheckCircle2 size={20} />,
      color: DS.green,
      bg: `linear-gradient(135deg,${DS.greenLight},#EAFAF0)`,
      border: "#B0E8C8",
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
     <style>{`
  .dr-row:hover { background: ${DS.surfaceAlt} !important; }
  @keyframes dr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @media (max-width: 1024px) {
    .dr-kpi-strip { grid-template-columns: repeat(2,1fr) !important; }
  }
  @media (max-width: 768px) {
    .dr-hide-sm { display: none !important; }
    .dr-page-pad { padding: 14px 10px !important; }
  }
  @media (max-width: 480px) {
    .dr-kpi-strip { grid-template-columns: 1fr 1fr !important; }
  }
`}</style>

      <div className="dr-page-pad" style={{ padding: "28px 32px", minHeight: "100vh", background: "#F4F0FA" }}>

        {/* ── PAGE HEADER ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${DS.red},${DS.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: DS.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Reports & Approvals
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: DS.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Deleted Reports
              </h1>
              <span style={{ background: DS.redLight, color: DS.red, fontSize: 9, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em", textTransform: "uppercase", border: `1px solid ${DS.red}30` }}>
                TRASH
              </span>
            </div>
            <p style={{ color: DS.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              All reports that have been deleted from the system
            </p>
          </div>

<button
  onClick={async () => {
    setRefreshing(true);
    const minSpin = new Promise(resolve => setTimeout(resolve, 700));
    await Promise.all([fetchDeleted(), minSpin]);
    setRefreshing(false);
  }}
  disabled={refreshing}
  style={{
    width: 40, height: 40, borderRadius: 11,
    border: `1.5px solid ${DS.border}`,
    background: "#fff", color: DS.muted,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
  }}
  title="Refresh"
>
  <span style={{
    display: "inline-flex",
    animation: refreshing ? "dr-spin 0.7s linear infinite" : "none",
  }}>
    <RefreshCw size={16} />
  </span>
</button>
        </div>

        {/* ── KPI STRIP — matching ProjectReports.jsx card structure exactly ── */}
        <div className="dr-kpi-strip" style={{
          display: "grid", gridTemplateColumns: "repeat(4,1fr)",
          gap: 16, marginBottom: 24,
        }}>
          {kpiCards.map((k) => (
            <div key={k.label} style={{
              background: DS.surface,
              borderRadius: 16,
              border: `1px solid ${DS.borderSolid}`,
              padding: "20px 22px",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
              position: "relative", overflow: "hidden",
            }}>
              {/* Background accent blob — same as ProjectReports */}
              <div style={{
                position: "absolute", right: -10, top: -10,
                width: 70, height: 70, borderRadius: "50%",
                background: k.bg, opacity: 0.6,
              }} />
              <div style={{
                width: 46, height: 46, borderRadius: 13,
                background: k.bg, border: `1.5px solid ${k.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0, position: "relative",
              }}>{k.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: DS.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {k.label}
                </div>
                <div style={{ fontWeight: 800, fontSize: 28, color: k.color, lineHeight: 1 }}>
                  {k.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── TABLE CARD ── */}
        <div style={{ background: "#fff", borderRadius: 20, border: `1px solid ${DS.border}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(92,45,145,0.08)" }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${DS.violet},${DS.red},${DS.orange})` }} />

          {/* Table toolbar */}
          <div style={{ padding: "18px 24px", borderBottom: `1px solid ${DS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>Deleted Report Records</div>
              <div style={{ fontSize: 12, color: DS.muted, marginTop: 2 }}>
                {filtered.length} of {reports.length} records
                {search && <span style={{ color: DS.violet, fontWeight: 600 }}> · filtered</span>}
              </div>
            </div>

            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`, borderRadius: 12, padding: "9px 14px", minWidth: 280 }}>
<Search size={14} color={DS.faint} />              <input
                type="text"
                placeholder="Search report, project, engineer, type…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}   // was: onChange={e => setSearch(e.target.value)}
                style={{ border: "none", background: "transparent", fontSize: 13, color: DS.dark, outline: "none", width: "100%", fontFamily: "inherit" }}
              />
              {search && (
                <button onClick={() => { setSearch(""); setPage(1); }}
  style={{ background: DS.violetLight, border: "none", color: DS.violet, cursor: "pointer", width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
  <X size={11} />
</button>
              )}
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "64px 0", color: DS.muted, fontSize: 14 }}>
  <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}>
    <Clock size={32} color={DS.muted} />
  </div>
  Loading deleted reports…
</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "72px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
<div style={{ width: 64, height: 64, borderRadius: 18, background: DS.redLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
  <Trash2 size={28} color={DS.red} />
</div>              <div style={{ fontWeight: 700, fontSize: 16, color: DS.dark }}>
                {search ? "No matching records" : "No deleted reports"}
              </div>
              <div style={{ fontSize: 13, color: DS.muted, maxWidth: 300, lineHeight: 1.6 }}>
                {search ? `No results for "${search}". Try a different search.` : "Reports you delete will appear here."}
              </div>
             {search && (
                <button onClick={() => { setSearch(""); setPage(1); }}
                  style={{ background: `linear-gradient(135deg,${DS.violet},${DS.violetMid})`, color: "#fff", border: "none", padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 820 }}>
                <thead>
                  <tr style={{ background: "#FAF8FD" }}>
                    {["#", "Report Title", "Type / Stage", "Project", "Engineer", "Deleted By", "Deleted At", "Details"].map((h, hi) => (
                      <th key={h} className={hi >= 5 ? "dr-hide-sm" : ""} style={{ fontSize: 10, fontWeight: 700, color: DS.muted, textTransform: "uppercase", letterSpacing: "0.09em", padding: "12px 16px", textAlign: "left", borderBottom: `2px solid ${DS.border}`, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                 
                  {paged.map((r, i) => {
                    const sc = stageCfg(r.report_stage);
                    const isLast = i === paged.length - 1;
                    const rowNum = (page - 1) * PER_PAGE + i + 1;
                    return (
                      <tr key={r.id} className="dr-row"
                        onMouseEnter={() => setHovRow(r.id)}
                        onMouseLeave={() => setHovRow(null)}
                        style={{ background: "#fff", transition: "background 0.12s" }}>

                        {/* # */}
                        <td style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, background: DS.redLight, fontWeight: 700, fontSize: 11, color: DS.red }}>
                            {rowNum}
                          </span>
                        </td>

{/* Report Title */}
                        <td style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: DS.dark, marginBottom: 3 }}>{r.title || "—"}</div>
                          <div style={{ fontSize: 11, color: DS.muted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.details}>
                            {r.details || "—"}
                          </div>
                        </td>

                        {/* Type / Stage */}
                        <td style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: DS.dark, marginBottom: 4 }}>{r.report_type || "—"}</div>
                          <span style={{ fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.color, padding: "3px 10px", borderRadius: 20, display: "inline-block" }}>
                            {sc.label}
                          </span>
                        </td>

                        {/* Project */}
                        <td style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: DS.violet }}>{r.project_uid || "—"}</div>
                          <div style={{ fontSize: 11, color: DS.muted, marginTop: 2, maxWidth: 160 }}>{r.project_name || "—"}</div>
                        </td>

                        {/* Engineer */}
                        <td style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg,${DS.violet},${DS.violetMid})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                              {(r.engineer_name || "?").charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: DS.dark }}>{r.engineer_name || "—"}</span>
                          </div>
                        </td>

                        {/* Deleted By */}
                        <td className="dr-hide-sm" style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: DS.red }}>{r.deleted_by_name || "—"}</div>
                        </td>

                        {/* Deleted At */}
                        <td className="dr-hide-sm" style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ fontSize: 12, color: DS.muted, fontWeight: 500 }}>{fmtDate(r.deleted_at)}</div>
                        </td>

                        {/* Details */}
                        <td style={{ padding: "14px 16px", borderBottom: isLast ? "none" : `1px solid ${DS.border}` }}>
                          <div style={{ fontSize: 12, color: DS.gray, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.details}>
                            {r.details || "—"}
                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {/* Footer — pagination */}
          {filtered.length > 0 && (
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${DS.border}`, background: DS.surfaceAlt, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <span style={{ fontSize: 12, color: DS.muted, fontWeight: 500 }}>
                Showing{" "}
                <strong style={{ color: DS.dark }}>
                  {Math.min((page - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(page * PER_PAGE, filtered.length)}
                </strong>
                {" "}of{" "}
                <strong style={{ color: DS.dark }}>{filtered.length}</strong>
                {" "}deleted reports
              </span>

              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    height: 34, padding: "0 14px", borderRadius: 10,
                    border: `1.5px solid ${page === 1 ? DS.borderSolid : DS.violet}`,
                    background: page === 1 ? DS.surface : DS.violetLight,
                    color: page === 1 ? DS.faint : DS.violet,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                  }}
                >‹ Prev</button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                  .map((n) => (
                    <button key={n} onClick={() => setPage(n)} style={{
                      width: 34, height: 34, borderRadius: 10,
                      border: `1.5px solid ${n === page ? DS.violet : DS.borderSolid}`,
                      background: n === page ? `linear-gradient(135deg,${DS.violet},${DS.violetMid})` : DS.surface,
                      color: n === page ? "#fff" : DS.muted,
                      fontSize: 12, fontWeight: 700, cursor: "pointer",
                      boxShadow: n === page ? `0 2px 8px ${DS.violet}40` : "none",
                      transition: "all 0.15s",
                    }}>{n}</button>
                  ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    height: 34, padding: "0 14px", borderRadius: 10,
                    border: `1.5px solid ${page === totalPages ? DS.borderSolid : DS.violet}`,
                    background: page === totalPages ? DS.surface : DS.violetLight,
                    color: page === totalPages ? DS.faint : DS.violet,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                  }}
                >Next ›</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </AdminLayout>
  );
}